import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Info as InfoIcon } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FeaturedQAPreview from "@/components/FeaturedQAPreview";
import { STAGES, formatDateShortUK } from "./_helpers";

const INFO_FIELDS = [
  { key: "venue_name", label: "Venue name", type: "input" },
  { key: "venue_address", label: "Venue address", type: "input" },
  { key: "venue_map_url", label: "Venue map image URL", type: "input", placeholder: "https://…" },
  { key: "welcome_reception", label: "Welcome reception", type: "textarea" },
  { key: "conference_party", label: "Conference party", type: "textarea" },
  { key: "storytelling_evening", label: "Storytelling evening", type: "textarea" },
  { key: "dress_code", label: "Dress code", type: "textarea" },
  { key: "dietary_info", label: "Dietary info", type: "textarea" },
  { key: "code_of_conduct", label: "Code of conduct", type: "textarea" },
  { key: "contact_email", label: "Contact email", type: "input", placeholder: "hello@dftbdigital.com" },
  { key: "spotify_playlist_url", label: "Spotify playlist URL", type: "input", placeholder: "https://open.spotify.com/playlist/…" },
];

export default function InfoAdmin() {
  const [info, setInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/info").then((r) => {
      setInfo({ faqs: [], ...r.data });
    }).catch((err) => toast.error(formatApiError(err)));
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  const setField = (key, value) => setInfo((cur) => ({ ...cur, [key]: value }));
  const setStream = (stage, value) => setInfo((cur) => ({
    ...cur,
    live_streams: { ...(cur.live_streams || {}), [stage]: value },
  }));
  const setStageLabel = (stage, value) => setInfo((cur) => ({
    ...cur,
    stage_labels: { ...(cur.stage_labels || {}), [stage]: value },
  }));
  const setFaq = (idx, patch) => setInfo((cur) => {
    const faqs = [...(cur.faqs || [])];
    faqs[idx] = { ...faqs[idx], ...patch };
    return { ...cur, faqs };
  });
  const addFaq = () => setInfo((cur) => ({ ...cur, faqs: [...(cur.faqs || []), { q: "", a: "" }] }));
  const removeFaq = (idx) => setInfo((cur) => ({ ...cur, faqs: (cur.faqs || []).filter((_, i) => i !== idx) }));
  const moveFaq = (idx, delta) => setInfo((cur) => {
    const faqs = [...(cur.faqs || [])];
    const j = idx + delta;
    if (j < 0 || j >= faqs.length) return cur;
    [faqs[idx], faqs[j]] = [faqs[j], faqs[idx]];
    return { ...cur, faqs };
  });

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty FAQs so we don't persist blanks.
      const payload = { ...info, faqs: (info.faqs || []).filter((f) => (f.q || "").trim() || (f.a || "").trim()) };
      const { data } = await api.patch("/info", payload);
      setInfo({ faqs: [], ...data });
      toast.success("Info page updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!info) return <div className="text-center text-[#7A828C] py-6">Loading…</div>;

  return (
    <div data-testid="info-admin" className="space-y-5">
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center gap-2 mb-3">
          <InfoIcon size={16} weight="duotone" className="text-[#000000]" />
          <h3 className="font-display text-base">Practical info</h3>
        </div>
        <p className="text-xs text-[#5a5a5a] mb-4">Edit what delegates see on the in-app Info tab. Saves immediately when you click Save.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {INFO_FIELDS.map(({ key, label, type, placeholder }) => (
            <div key={key} className={type === "textarea" ? "sm:col-span-2" : ""}>
              <Label htmlFor={`info-${key}`}>{label}</Label>
              {type === "textarea" ? (
                <Textarea
                  id={`info-${key}`}
                  value={info[key] || ""}
                  onChange={(e) => setField(key, e.target.value)}
                  className="mt-1 rounded-sm"
                  rows={3}
                  data-testid={`info-${key}-input`}
                />
              ) : (
                <Input
                  id={`info-${key}`}
                  value={info[key] || ""}
                  onChange={(e) => setField(key, e.target.value)}
                  placeholder={placeholder}
                  className="h-11 rounded-sm mt-1"
                  data-testid={`info-${key}-input`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <h3 className="font-display text-base mb-1">Room names</h3>
        <p className="text-xs text-[#5a5a5a] mb-3">
          Rename a stage for delegates (e.g. <em>&quot;Concurrent 1&quot;</em> → <em>&quot;Bubbles Room&quot;</em>). Leave blank to keep the default. The internal IDs stay the same, so sessions and live streams aren&apos;t affected.
        </p>
        <div className="space-y-3">
          {STAGES.map((stage) => (
            <div key={stage} className="grid grid-cols-[110px_1fr] items-center gap-3">
              <Label className="text-xs font-mono-time uppercase tracking-widest text-[#7A828C]" htmlFor={`stage-label-${stage}`}>
                {stage}
              </Label>
              <Input
                id={`stage-label-${stage}`}
                value={(info.stage_labels || {})[stage] || ""}
                onChange={(e) => setStageLabel(stage, e.target.value)}
                placeholder={`Default: ${stage}`}
                className="h-11 rounded-sm"
                data-testid={`info-stage-label-${stage.toLowerCase().replace(/ /g, "-")}-input`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-display text-base">Live stream URLs</h3>
        </div>
        <p className="text-xs text-[#5a5a5a] mb-3">
          Vimeo Event embed URL for each stage. The embed appears inside session detail pages from 15 min before start until 15 min after end. <strong>Plenary and Concurrent 1 share a feed</strong> - paste the same URL into both.
        </p>
        <div className="space-y-3">
          {STAGES.map((stage) => (
            <div key={stage}>
              <Label htmlFor={`stream-${stage}`} className="text-xs font-mono-time uppercase tracking-widest text-[#7A828C]">{stage}</Label>
              <Input
                id={`stream-${stage}`}
                value={(info.live_streams || {})[stage] || ""}
                onChange={(e) => setStream(stage, e.target.value)}
                placeholder="https://vimeo.com/event/…/embed/…"
                className="h-11 rounded-sm mt-1 font-mono-time text-sm"
                data-testid={`info-stream-${stage.toLowerCase().replace(/ /g, "-")}-input`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <h3 className="font-display text-base mb-1">Featured Q&amp;A session</h3>
        <p className="text-xs text-[#5a5a5a] mb-3">
          Pick one session that accepts questions throughout the whole conference (e.g. an &ldquo;Ask the Experts&rdquo; panel). Delegates see a prominent <em>&quot;Submit a question to&hellip;&quot;</em> card on the Programme. Leave blank to disable.
        </p>
        <Select
          value={info.featured_qa_session_id || "__none__"}
          onValueChange={(v) => setField("featured_qa_session_id", v === "__none__" ? "" : v)}
        >
          <SelectTrigger className="h-11 rounded-sm" data-testid="info-featured-qa-select">
            <SelectValue placeholder="- None -" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="__none__">- None -</SelectItem>
            {[...sessions].sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`)).map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {formatDateShortUK(s.date)} {s.start_time} · {s.stage_label || s.stage} · {s.title.slice(0, 60)}{s.title.length > 60 ? "…" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {info.featured_qa_session_id && (
        <FeaturedQAPreview sessionId={info.featured_qa_session_id} />
      )}

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base">FAQs</h3>
          <Button onClick={addFaq} variant="outline" size="sm" className="rounded-sm" data-testid="info-add-faq">
            <Plus size={14} className="mr-1" /> Add FAQ
          </Button>
        </div>
        {(info.faqs || []).length === 0 && (
          <p className="text-xs text-[#7A828C]">No FAQs yet. Add one with the button above.</p>
        )}
        <div className="space-y-3">
          {(info.faqs || []).map((faq, idx) => (
            <div key={idx} className="border border-[#E5E9F0] rounded-sm p-3 space-y-2" data-testid={`info-faq-${idx}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono-time uppercase tracking-widest text-[#7A828C]">FAQ {idx + 1}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveFaq(idx, -1)} disabled={idx === 0} aria-label="Move up" className="p-1 text-[#7A828C] hover:text-[#000000] disabled:opacity-30" data-testid={`info-faq-${idx}-up`}>↑</button>
                  <button onClick={() => moveFaq(idx, 1)} disabled={idx === (info.faqs.length - 1)} aria-label="Move down" className="p-1 text-[#7A828C] hover:text-[#000000] disabled:opacity-30" data-testid={`info-faq-${idx}-down`}>↓</button>
                  <button onClick={() => removeFaq(idx)} aria-label="Remove FAQ" className="p-1 text-[#DC4F48] hover:opacity-80" data-testid={`info-faq-${idx}-remove`}><Trash size={14} /></button>
                </div>
              </div>
              <Input
                placeholder="Question"
                value={faq.q || ""}
                onChange={(e) => setFaq(idx, { q: e.target.value })}
                className="h-11 rounded-sm"
                data-testid={`info-faq-${idx}-q`}
              />
              <Textarea
                placeholder="Answer"
                value={faq.a || ""}
                onChange={(e) => setFaq(idx, { a: e.target.value })}
                rows={2}
                className="rounded-sm"
                data-testid={`info-faq-${idx}-a`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] shadow-lg"
          data-testid="info-save-btn"
        >
          {saving ? "Saving…" : "Save info page"}
        </Button>
      </div>
    </div>
  );
}
